'use client';
import type { BlogCategory } from '@eliadmin/core/src/api/blogs/blogs-types';
import { useBlogCategories } from '@eliadmin/core/src/components/blog/blog-cards';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { Cross2Icon } from '@radix-ui/react-icons';
import { usePathname, useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';

const PAGE_SIZE = 10;

export const BlogCategories = () => {
  const searchParams = useSearchParams();
  const { t } = useTranslations();
  const pathname = usePathname();

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const activeCategoryIds = useBlogCategories(state => state.activeCategoryIds);

  const blogsCategories = useBlogCategories(state => state.categories);
  const setActiveCategories = useBlogCategories(state => state.setActiveCategories);

  useEffect(() => {
    const newSearchParams = new URLSearchParams(window.location.search);

    if (!activeCategoryIds || activeCategoryIds.length === 0) {
      newSearchParams.delete('filter');
    } else {
      newSearchParams.set('filter', `categories.id IN [${activeCategoryIds.join(',')}]`);
    }

    const newSearchString = newSearchParams.toString();
    const currentSearchString = window.location.search.substring(1);

    if (newSearchString === currentSearchString) {
      return;
    }

    const newUrl = `${window.location.pathname}${newSearchString ? `?${newSearchString}` : ''}`;

    if (window.location.href !== newUrl) {
      window.history.replaceState({}, '', newUrl);
    }
  }, [activeCategoryIds]);

  const toggleCategory = (category: BlogCategory) => {
    setActiveCategories(
      activeCategoryIds.includes(category.id)
        ? activeCategoryIds.filter(c => c !== category.id)
        : [...activeCategoryIds, category.id],
    );
  };

  const isActiveCategory = (category: BlogCategory) => {
    return activeCategoryIds.filter(c => c === category.id).length > 0;
  };

  const nonActiveCategories = blogsCategories.filter(
    (category: any) => !isActiveCategory(category),
  );

  return (
    <div>
      <div className="flex w-full flex-wrap items-start justify-start gap-x-2 gap-y-4">
        {blogsCategories
          .filter((category: any) => isActiveCategory(category))
          .map((category: any) => (
            <Button
              key={category.id}
              onClick={() => toggleCategory(category)}
              className="h-fit rounded-full bg-[hsl(var(--primary))] py-1 pl-1 pr-3 text-[12px] font-medium text-white"
            >
              <Cross2Icon className="inline-block" />
              {category.name}
            </Button>
          ))}
        {nonActiveCategories.slice(0, visibleCount).map(category => (
          <Button
            key={category.id}
            onClick={() => toggleCategory(category)}
            className="h-fit rounded-full bg-[hsl(var(--primary)/0.11)] px-3 py-1 text-[12px] font-medium text-[hsl(var(--primary))] hover:text-white"
          >
            {category.name}
          </Button>
        ))}
        <div className="flex w-full justify-end">
          {nonActiveCategories.length > visibleCount && (
            <Button size="xs" onClick={() => setVisibleCount(visibleCount + PAGE_SIZE)}>
              {t('text338')}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};
